/**
 * Session Manager for Analytics
 *
 * Manages first-party session IDs with 30-minute inactivity expiry.
 * Session ID is persisted in localStorage with activity timestamp.
 */

const SESSION_STORAGE_KEY = 'mcs_analytics_session';
const SESSION_ACTIVITY_KEY = 'mcs_session_activity';
const SESSION_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes in milliseconds

interface SessionData {
  sessionId: string;
  createdAt: number;
  lastActivityAt: number;
}

// Cached session data to avoid repeated localStorage reads
let cachedSession: SessionData | null = null;
let cachedUserId: string | null = null;
let userIdFetched = false;

/**
 * Generate a unique session ID
 */
const generateSessionId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  const randomPart2 = Math.random().toString(36).substring(2, 15);
  return `sess_${timestamp}_${randomPart}${randomPart2}`;
};

/**
 * Check if session has expired due to inactivity
 */
const isSessionExpired = (session: SessionData): boolean => {
  const now = Date.now();
  return now - session.lastActivityAt > SESSION_EXPIRY_MS;
};

/**
 * Get the current session, creating a new one if needed
 */
export const getSession = (): SessionData => {
  // Return cached session if valid
  if (cachedSession && !isSessionExpired(cachedSession)) {
    return cachedSession;
  }

  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const session: SessionData = JSON.parse(stored);

      // Check if session has expired
      if (!isSessionExpired(session)) {
        cachedSession = session;
        return session;
      }
    }
  } catch (error) {
    console.debug('Session Manager: Error reading session from storage', error);
  }

  // Create new session
  const newSession: SessionData = {
    sessionId: generateSessionId(),
    createdAt: Date.now(),
    lastActivityAt: Date.now(),
  };

  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
  } catch (error) {
    console.debug('Session Manager: Error saving session to storage', error);
  }

  cachedSession = newSession;
  return newSession;
};

/**
 * Get the current session ID
 */
export const getSessionId = (): string => {
  return getSession().sessionId;
};

/**
 * Update session activity timestamp (call this on user interaction)
 */
export const updateSessionActivity = (): void => {
  const session = getSession();
  session.lastActivityAt = Date.now();

  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    cachedSession = session;
  } catch (error) {
    console.debug('Session Manager: Error updating session activity', error);
  }
};

/**
 * Get cached user ID (set after initial auth check)
 */
export const getCachedUserId = (): string | null => {
  return cachedUserId;
};

/**
 * Check if user ID has been fetched
 */
export const hasUserIdBeenFetched = (): boolean => {
  return userIdFetched;
};

/**
 * Set the cached user ID (call once per session after auth check)
 */
export const setCachedUserId = (userId: string | null): void => {
  cachedUserId = userId;
  userIdFetched = true;
};

/**
 * Clear cached user ID (call on sign out)
 */
export const clearCachedUserId = (): void => {
  cachedUserId = null;
  userIdFetched = false;
};

/**
 * Force create a new session (useful for testing or explicit session reset)
 */
export const createNewSession = (): SessionData => {
  const newSession: SessionData = {
    sessionId: generateSessionId(),
    createdAt: Date.now(),
    lastActivityAt: Date.now(),
  };

  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
  } catch (error) {
    console.debug('Session Manager: Error creating new session', error);
  }

  cachedSession = newSession;
  return newSession;
};

/**
 * Get session age in milliseconds
 */
export const getSessionAge = (): number => {
  const session = getSession();
  return Date.now() - session.createdAt;
};

/**
 * Get time since last activity in milliseconds
 */
export const getTimeSinceLastActivity = (): number => {
  const session = getSession();
  return Date.now() - session.lastActivityAt;
};

/**
 * Check if session is about to expire (within 5 minutes)
 */
export const isSessionAboutToExpire = (): boolean => {
  const session = getSession();
  const timeUntilExpiry = SESSION_EXPIRY_MS - (Date.now() - session.lastActivityAt);
  return timeUntilExpiry < 5 * 60 * 1000; // 5 minutes
};

// Initialize session on module load
if (typeof window !== 'undefined') {
  // Get or create session on load
  getSession();

  // Update activity on user interactions
  const updateActivity = () => {
    updateSessionActivity();
  };

  // Debounced activity update
  let activityTimeout: ReturnType<typeof setTimeout> | null = null;
  const debouncedActivityUpdate = () => {
    if (activityTimeout) {
      clearTimeout(activityTimeout);
    }
    activityTimeout = setTimeout(updateActivity, 1000);
  };

  // Listen for user activity events
  window.addEventListener('click', debouncedActivityUpdate, { passive: true });
  window.addEventListener('scroll', debouncedActivityUpdate, { passive: true });
  window.addEventListener('keydown', debouncedActivityUpdate, { passive: true });
  window.addEventListener('touchstart', debouncedActivityUpdate, { passive: true });
}

export default {
  getSession,
  getSessionId,
  updateSessionActivity,
  getCachedUserId,
  setCachedUserId,
  clearCachedUserId,
  hasUserIdBeenFetched,
  createNewSession,
  getSessionAge,
  getTimeSinceLastActivity,
  isSessionAboutToExpire,
};
